'use client';

import { useState } from 'react';
import { AlertTriangle, RefreshCw, Home, Send } from 'lucide-react';

// ============================================================================
// ErrorFallback — the single shared red error UI used across the app.
//
// Renders the red card with:
//   • Error message + digest
//   • Auto-reported reference number (when available)
//   • Expandable technical stack trace + component stack
//   • Try Again / Reload / Go Home action buttons
//
// Two modes:
//   • full   (default) — `min-h-screen`, for route-level error.tsx pages
//   • compact         — `min-h-[60vh]`, for embedded module/section errors
//
// This component is purely presentational. The auto-reporting logic lives in
// the `useErrorReport` hook (for function components / error.tsx) or in the
// `ErrorBoundary` class (for component-level boundaries).
// ============================================================================

export interface ErrorFallbackProps {
  error: Error & { digest?: string };
  /** React component stack from class error boundaries (componentDidCatch). */
  componentStack?: string | null;
  /** Reference number returned by the error reporting service. */
  reportReference?: string | null;
  /** True while the error report is being submitted. */
  isReporting?: boolean;
  /** Card title (default: "Something Went Wrong"). */
  title?: string;
  /** Subtitle below the title. */
  subtitle?: string;
  /** Compact mode for embedded use (default: false = full-screen). */
  compact?: boolean;
  /** "Try Again" handler. Omit to hide the button. */
  onReset?: () => void;
  /** "Reload Page" handler (default: window.location.reload). */
  onReload?: () => void;
  /** "Go Home" handler (default: /client-zone/dashboard). */
  onGoHome?: () => void;
  /** Initially show technical details expanded (default: false). */
  defaultShowDetails?: boolean;
}

export function ErrorFallback({
  error,
  componentStack,
  reportReference,
  isReporting = false,
  title = 'Something Went Wrong',
  subtitle = 'We encountered an unexpected error. Don\'t worry, your data is safe.',
  compact = false,
  onReset,
  onReload,
  onGoHome,
  defaultShowDetails = false,
}: ErrorFallbackProps) {
  const [showDetails, setShowDetails] = useState(defaultShowDetails);

  const handleReload = onReload ?? (() => window.location.reload());
  const handleGoHome = onGoHome ?? (() => { window.location.href = '/client-zone/dashboard'; });

  return (
    <div className={`flex items-center justify-center p-4 ${compact ? 'min-h-[60vh]' : 'min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900'}`}>
      <div className={`w-full max-w-2xl overflow-hidden rounded-xl border border-red-200 bg-white shadow-xl dark:border-red-900 dark:bg-gray-800 ${compact ? '' : 'shadow-2xl'}`}>
        {/* Header */}
        <div className="bg-red-600 p-6">
          <div className="flex items-center gap-4 text-white">
            <div className="rounded-lg bg-white/20 p-3">
              <AlertTriangle className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{title}</h1>
              <p className="mt-1 text-sm text-red-100">{subtitle}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error message */}
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-left dark:border-red-800 dark:bg-red-950/20">
            <div className="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">
              Message
            </div>
            <p className="mt-1 break-words text-sm text-red-900 dark:text-red-100">
              {error.message || 'An unexpected error occurred. Please try reloading the page.'}
            </p>
          </div>

          {error.digest && (
            <p className="mb-4 font-mono text-xs text-gray-500 dark:text-gray-400">
              Error ID: {error.digest}
            </p>
          )}

          {/* Report status */}
          {reportReference ? (
            <div className="mb-6 rounded-lg border-2 border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/20">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-800">
                  <Send className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="text-left">
                  <h4 className="mb-1 font-semibold text-green-900 dark:text-green-100">Error Report Submitted</h4>
                  <p className="mb-2 text-sm text-green-700 dark:text-green-300">
                    Our team has been notified and will investigate this issue.
                  </p>
                  <div className="rounded border border-green-300 bg-white p-3 dark:border-green-700 dark:bg-gray-800">
                    <div className="mb-1 text-xs font-semibold text-green-600 dark:text-green-400">Reference Number:</div>
                    <code className="text-lg font-bold text-green-900 dark:text-green-100">{reportReference}</code>
                  </div>
                </div>
              </div>
            </div>
          ) : isReporting ? (
            <div className="mb-6 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Submitting error report…
            </div>
          ) : null}

          {/* Technical details toggle */}
          {showDetails && (
            <details className="mb-4 text-left" open>
              <summary className="mb-2 cursor-pointer text-sm font-semibold text-gray-700 dark:text-gray-300">
                Technical Details
              </summary>
              <div className="space-y-3 rounded bg-gray-50 p-3 dark:bg-gray-900">
                {error.stack && (
                  <div>
                    <div className="mb-1 text-xs font-semibold text-gray-600 dark:text-gray-400">Stack Trace:</div>
                    <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words text-xs text-gray-700 dark:text-gray-300">
                      {error.stack}
                    </pre>
                  </div>
                )}
                {componentStack && (
                  <div>
                    <div className="mb-1 text-xs font-semibold text-gray-600 dark:text-gray-400">Component Stack:</div>
                    <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words text-xs text-gray-700 dark:text-gray-300">
                      {componentStack}
                    </pre>
                  </div>
                )}
                {!error.stack && !componentStack && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No technical stack details were available for this error.</p>
                )}
              </div>
            </details>
          )}

          <button
            onClick={() => setShowDetails(!showDetails)}
            className="mb-4 text-xs text-gray-500 underline hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            {showDetails ? 'Hide' : 'Show'} technical details
          </button>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3">
            {onReset && (
              <button
                onClick={onReset}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white transition-colors hover:bg-blue-700"
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </button>
            )}
            <button
              onClick={handleReload}
              className="flex items-center gap-2 rounded-lg bg-gray-600 px-4 py-2 font-semibold text-white transition-colors hover:bg-gray-700"
            >
              <RefreshCw className="h-4 w-4" />
              Reload Page
            </button>
            <button
              onClick={handleGoHome}
              className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <Home className="h-4 w-4" />
              Go Home
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 text-center text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
          Error occurred at {new Date().toLocaleString()}
        </div>
      </div>
    </div>
  );
}
